import { useState, useEffect, useRef, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiRequest, getSession } from '@/lib/supabase'
import { AnimatedGradient } from '@/components/ui/AnimatedGradient'
import Chart from 'chart.js/auto'

interface AnalyticsOverview {
  total_clicks: number
  unique_visitors: number
  total_links: number
  links_this_month: number
  custom_domains: number
  plan_name: string
}

interface TopLink {
  id: string
  shortcode: string
  original_url: string
  clicks: number
  custom_domain: string | null
  created_at: string
}

interface Referrer {
  referrer: string
  clicks: number
  percentage: number
}

interface ChartData {
  clicks_over_time: {
    labels: string[]
    data: number[]
  }
  devices: Record<string, number>
}

interface BreakdownItem {
  name: string
  clicks: number
  percentage: number
}

interface CountryItem {
  country: string
  code: string
  clicks: number
  percentage: number
}

interface Breakdowns {
  countries: CountryItem[]
  browsers: BreakdownItem[]
  os: BreakdownItem[]
  utm: {
    sources: { name: string; clicks: number }[]
    mediums: { name: string; clicks: number }[]
    campaigns: { name: string; clicks: number }[]
  }
}

interface RecentClick {
  clicked_at: string
  country: string | null
  country_code: string | null
  city: string | null
  device_type: string | null
  browser: string | null
  os: string | null
  referrer_domain: string | null
  shortcode: string | null
}

// Country code to flag emoji
function countryFlag(code: string | null) {
  if (!code || code.length !== 2) return ''
  return String.fromCodePoint(
    ...code.toUpperCase().split('').map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
  )
}

function timeAgo(dateStr: string) {
  const now = Date.now()
  const diff = now - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

// Stat card for the hero section
function StatCard({ icon, label, value, sub }: { icon: string; label: string; value: string | number; sub?: string }) {
  return (
    <div className="relative bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/60 dark:border-white/10 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center gap-2 mb-3">
        <span className="material-symbols-outlined text-[18px] text-[#2b6cee] dark:text-blue-400">{icon}</span>
        <span className="text-xs font-medium uppercase tracking-wider text-[#4c669a] dark:text-gray-400">{label}</span>
      </div>
      <p className="text-2xl font-bold text-[#0d121b] dark:text-white">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {sub && <p className="text-xs text-[#4c669a] dark:text-gray-500 mt-1">{sub}</p>}
    </div>
  )
}

// Horizontal bar for breakdown items
function BreakdownBar({ label, percentage, clicks, color }: { label: string; percentage: number; clicks: number; color: string }) {
  return (
    <div className="group">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-sm font-medium text-[#0d121b] dark:text-white truncate max-w-[60%]">{label}</span>
        <span className="text-xs text-[#4c669a] dark:text-gray-400 font-medium">
          {clicks.toLocaleString()} <span className="text-[#4c669a]/60">({percentage}%)</span>
        </span>
      </div>
      <div className="h-2 bg-gray-100 dark:bg-white/5 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.max(percentage, 2)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  )
}

// Section card wrapper
function SectionCard({ title, icon, children, className = '' }: { title: string; icon: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white dark:bg-[#0d121b] rounded-2xl border border-[#e7ebf3] dark:border-white/8 shadow-sm p-6 ${className}`}>
      <div className="flex items-center gap-2 mb-5">
        <span className="material-symbols-outlined text-[18px] text-[#2b6cee] dark:text-blue-400">{icon}</span>
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#0d121b] dark:text-white">{title}</h3>
      </div>
      {children}
    </div>
  )
}

export function AnalyticsPage() {
  const [dateRange, setDateRange] = useState(30)
  const [exportOpen, setExportOpen] = useState(false)

  const handleExport = useCallback(async (format: 'csv' | 'json') => {
    setExportOpen(false)
    const session = await getSession()
    if (!session) return

    const res = await fetch(`/api/analytics/export?format=${format}&days=${dateRange}`, {
      headers: { Authorization: `Bearer ${session.access_token}` }
    })

    if (!res.ok) return

    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `urlzippy-analytics-${new Date().toISOString().split('T')[0]}.${format}`
    a.click()
    URL.revokeObjectURL(url)
  }, [dateRange])

  const clicksChartRef = useRef<HTMLCanvasElement>(null)
  const devicesChartRef = useRef<HTMLCanvasElement>(null)
  const clicksChartInstance = useRef<Chart | null>(null)
  const devicesChartInstance = useRef<Chart | null>(null)

  // Fetch overview data
  const { data: overview, error: overviewError, isLoading: overviewLoading } = useQuery<AnalyticsOverview>({
    queryKey: ['analytics', 'overview'],
    queryFn: () => apiRequest('/api/analytics/overview')
  })

  // Fetch top links
  const { data: topLinks, error: topLinksError } = useQuery<TopLink[]>({
    queryKey: ['analytics', 'top-links'],
    queryFn: () => apiRequest('/api/analytics/top-links?limit=5')
  })

  // Fetch referrers
  const { data: referrers, error: referrersError } = useQuery<Referrer[]>({
    queryKey: ['analytics', 'referrers', dateRange],
    queryFn: () => apiRequest(`/api/analytics/referrers?limit=5&days=${dateRange}`)
  })

  // Fetch chart data
  const { data: chartData, error: chartDataError } = useQuery<ChartData>({
    queryKey: ['analytics', 'chart-data', dateRange],
    queryFn: () => apiRequest(`/api/analytics/chart-data?days=${dateRange}`)
  })

  // Fetch breakdowns (countries, browsers, OS, UTM)
  const { data: breakdowns } = useQuery<Breakdowns>({
    queryKey: ['analytics', 'breakdowns', dateRange],
    queryFn: () => apiRequest(`/api/analytics/breakdowns?days=${dateRange}`)
  })

  // Fetch recent clicks
  const { data: recentClicks } = useQuery<RecentClick[]>({
    queryKey: ['analytics', 'recent-clicks'],
    queryFn: () => apiRequest('/api/analytics/recent-clicks?limit=15')
  })

  // Initialize and update clicks chart
  useEffect(() => {
    if (!clicksChartRef.current || !chartData) return

    if (clicksChartInstance.current) {
      clicksChartInstance.current.destroy()
    }

    const ctx = clicksChartRef.current.getContext('2d')
    if (!ctx) return

    const gradient = ctx.createLinearGradient(0, 0, 0, 280)
    gradient.addColorStop(0, 'rgba(43, 108, 238, 0.15)')
    gradient.addColorStop(1, 'rgba(43, 108, 238, 0)')

    clicksChartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: chartData.clicks_over_time.labels,
        datasets: [{
          label: 'Clicks',
          data: chartData.clicks_over_time.data,
          borderColor: '#2b6cee',
          backgroundColor: gradient,
          fill: true,
          tension: 0.4,
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: '#2b6cee',
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: '#0d121b',
            titleFont: { size: 12, weight: 'normal' as const },
            bodyFont: { size: 14, weight: 'bold' as const },
            padding: 12,
            cornerRadius: 10,
            displayColors: false,
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            border: { display: false },
            grid: { color: 'rgba(0,0,0,0.04)' },
            ticks: { font: { size: 11 }, color: '#4c669a', padding: 8 }
          },
          x: {
            border: { display: false },
            grid: { display: false },
            ticks: { font: { size: 11 }, color: '#4c669a', maxRotation: 0, autoSkip: true, maxTicksLimit: 8 }
          }
        }
      }
    })

    return () => {
      if (clicksChartInstance.current) {
        clicksChartInstance.current.destroy()
      }
    }
  }, [chartData])

  // Initialize and update devices chart
  useEffect(() => {
    if (!devicesChartRef.current || !chartData) return

    if (devicesChartInstance.current) {
      devicesChartInstance.current.destroy()
    }

    const ctx = devicesChartRef.current.getContext('2d')
    if (!ctx) return

    const devices = chartData.devices
    const deviceColors: Record<string, string> = {
      desktop: '#2b6cee',
      mobile: '#10b981',
      tablet: '#f59e0b',
      bot: '#6b7280'
    }

    const labels = Object.keys(devices).map(d => d.charAt(0).toUpperCase() + d.slice(1))
    const data = Object.values(devices)
    const colors = Object.keys(devices).map(d => deviceColors[d] || '#6b7280')

    devicesChartInstance.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: colors,
          borderWidth: 0,
          spacing: 2,
          borderRadius: 4,
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '72%',
        plugins: {
          legend: {
            position: 'bottom',
            labels: {
              padding: 16,
              usePointStyle: true,
              pointStyleWidth: 8,
              font: { size: 12 },
              color: '#4c669a',
            }
          },
          tooltip: {
            backgroundColor: '#0d121b',
            bodyFont: { size: 13, weight: 'bold' as const },
            padding: 10,
            cornerRadius: 8,
          }
        }
      }
    })

    return () => {
      if (devicesChartInstance.current) {
        devicesChartInstance.current.destroy()
      }
    }
  }, [chartData])

  const topLinkClicks = topLinks?.[0]?.clicks || 0

  // Show error state if any critical query fails
  if (overviewError) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 max-w-md">
          <h3 className="text-red-800 dark:text-red-200 font-bold mb-2">Failed to Load Analytics</h3>
          <p className="text-red-600 dark:text-red-300 text-sm mb-4">
            {overviewError instanceof Error ? overviewError.message : 'An error occurred while loading analytics data.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  // Show loading state
  if (overviewLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-[#4c669a] dark:text-gray-400">Loading analytics...</div>
      </div>
    )
  }

  // Show empty state if no data
  const hasNoData = !overview?.total_clicks && (!topLinks || topLinks.length === 0)
  if (hasNoData) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <span className="material-symbols-outlined text-[48px] text-[#2b6cee]/30 mb-4 block">bar_chart</span>
          <h3 className="text-xl font-bold text-[#0d121b] dark:text-white mb-2">No Analytics Data Yet</h3>
          <p className="text-[#4c669a] dark:text-gray-400 mb-6">
            Create a short link and share it to see analytics data here. Your link performance metrics will appear once you have some traffic.
          </p>
          <a
            href="/dashboard"
            className="inline-block px-6 py-3 bg-[#2b6cee] text-white rounded-xl hover:bg-[#2260d4] transition-colors font-medium"
          >
            Create Your First Link
          </a>
        </div>
      </div>
    )
  }

  const deviceColors = ['#2b6cee', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']
  const hasUtmData = breakdowns?.utm && (
    breakdowns.utm.sources.length > 0 || breakdowns.utm.mediums.length > 0 || breakdowns.utm.campaigns.length > 0
  )

  return (
    <div className="space-y-8">
      {/* ── Hero Section with Animated Gradient ── */}
      <div className="relative rounded-3xl overflow-hidden">
        <AnimatedGradient
          colors={['#2b6cee', '#10b981', '#8b5cf6', '#f59e0b']}
          speed={8}
          blur="heavy"
        />
        <div className="relative z-10 p-6 md:p-8">
          {/* Header */}
          <div className="flex flex-wrap justify-between items-start gap-4 mb-8">
            <div>
              <h2 className="text-[#0d121b] dark:text-white text-3xl font-bold tracking-tight">Analytics</h2>
              <p className="text-[#4c669a] dark:text-gray-400 text-sm mt-1">
                Track your link performance and visitor insights
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex bg-white/60 dark:bg-white/5 backdrop-blur-sm rounded-xl p-1 border border-white/40 dark:border-white/10">
                {[7, 30, 90].map(days => (
                  <button
                    key={days}
                    onClick={() => setDateRange(days)}
                    className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      dateRange === days
                        ? 'bg-[#2b6cee] text-white shadow-sm'
                        : 'text-[#4c669a] dark:text-gray-400 hover:text-[#0d121b] dark:hover:text-white'
                    }`}
                  >
                    {days}d
                  </button>
                ))}
              </div>
              <div className="relative">
                <button
                  onClick={() => setExportOpen(!exportOpen)}
                  className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-sm font-medium bg-white/60 dark:bg-white/5 backdrop-blur-sm border border-white/40 dark:border-white/10 text-[#0d121b] dark:text-white hover:bg-white/80 dark:hover:bg-white/10 transition-all"
                >
                  <span className="material-symbols-outlined text-[16px]">download</span>
                  Export
                </button>
                {exportOpen && (
                  <div className="absolute right-0 mt-2 bg-white dark:bg-[#1c2230] border border-[#e7ebf3] dark:border-white/10 rounded-xl shadow-xl py-1 z-20 min-w-[150px]">
                    <button
                      onClick={() => handleExport('csv')}
                      className="w-full px-4 py-2.5 text-left text-sm text-[#0d121b] dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 flex items-center gap-2"
                    >
                      <span className="material-symbols-outlined text-[16px]">table_rows</span>
                      Export as CSV
                    </button>
                    <button
                      onClick={() => handleExport('json')}
                      className="w-full px-4 py-2.5 text-left text-sm text-[#0d121b] dark:text-white hover:bg-gray-50 dark:hover:bg-white/5 flex items-center gap-2"
                    >
                      <span className="material-symbols-outlined text-[16px]">data_object</span>
                      Export as JSON
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard icon="ads_click" label="Total Clicks" value={overview?.total_clicks || 0} />
            <StatCard icon="group" label="Unique Visitors" value={overview?.unique_visitors || 0} />
            <StatCard icon="link" label="Total Links" value={overview?.total_links || 0} />
            <StatCard icon="calendar_month" label="This Month" value={overview?.links_this_month || 0} sub="links created" />
            <StatCard icon="language" label="Domains" value={overview?.custom_domains || 0} sub="custom domains" />
            <StatCard icon="trending_up" label="Top Link" value={topLinkClicks} sub={topLinks?.[0]?.shortcode || '—'} />
          </div>
        </div>
      </div>

      {/* ── Charts Row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SectionCard title="Clicks Over Time" icon="show_chart" className="lg:col-span-2">
          {chartDataError ? (
            <div className="h-72 flex items-center justify-center text-red-500 text-sm">
              Failed to load chart data
            </div>
          ) : (
            <div className="h-72">
              <canvas ref={clicksChartRef} />
            </div>
          )}
        </SectionCard>

        <SectionCard title="Devices" icon="devices">
          {chartDataError ? (
            <div className="h-72 flex items-center justify-center text-red-500 text-sm">
              Failed to load device data
            </div>
          ) : (
            <div className="h-72 flex items-center justify-center">
              <canvas ref={devicesChartRef} />
            </div>
          )}
        </SectionCard>
      </div>

      {/* ── Breakdowns: Countries + Browsers ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Top Countries" icon="public">
          {breakdowns?.countries && breakdowns.countries.length > 0 ? (
            <div className="space-y-4">
              {breakdowns.countries.map((c, i) => (
                <div key={c.code || c.country} className="flex items-center gap-3">
                  <span className="text-lg w-7 text-center">{countryFlag(c.code)}</span>
                  <div className="flex-1 min-w-0">
                    <BreakdownBar
                      label={c.country}
                      percentage={c.percentage}
                      clicks={c.clicks}
                      color={deviceColors[i % deviceColors.length]}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#4c669a] dark:text-gray-500">No country data available yet</p>
          )}
        </SectionCard>

        <SectionCard title="Browsers" icon="web">
          {breakdowns?.browsers && breakdowns.browsers.length > 0 ? (
            <div className="space-y-4">
              {breakdowns.browsers.map((b, i) => (
                <BreakdownBar
                  key={b.name}
                  label={b.name}
                  percentage={b.percentage}
                  clicks={b.clicks}
                  color={deviceColors[i % deviceColors.length]}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#4c669a] dark:text-gray-500">No browser data available yet</p>
          )}
        </SectionCard>
      </div>

      {/* ── Breakdowns: OS + Referrers ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Operating Systems" icon="computer">
          {breakdowns?.os && breakdowns.os.length > 0 ? (
            <div className="space-y-4">
              {breakdowns.os.map((o, i) => (
                <BreakdownBar
                  key={o.name}
                  label={o.name}
                  percentage={o.percentage}
                  clicks={o.clicks}
                  color={deviceColors[i % deviceColors.length]}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#4c669a] dark:text-gray-500">No OS data available yet</p>
          )}
        </SectionCard>

        <SectionCard title="Top Referrers" icon="link">
          {referrersError ? (
            <p className="text-red-500 text-sm">Failed to load referrers</p>
          ) : referrers && referrers.length > 0 ? (
            <div className="space-y-4">
              {referrers.map((ref, i) => (
                <BreakdownBar
                  key={ref.referrer}
                  label={ref.referrer}
                  percentage={ref.percentage}
                  clicks={ref.clicks}
                  color={deviceColors[i % deviceColors.length]}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#4c669a] dark:text-gray-500">No referrer data available yet</p>
          )}
        </SectionCard>
      </div>

      {/* ── UTM Campaign Tracking ── */}
      {hasUtmData && (
        <SectionCard title="UTM Campaign Tracking" icon="campaign">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Sources */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-[#4c669a] dark:text-gray-400 mb-3">Sources</h4>
              <div className="space-y-2">
                {breakdowns!.utm.sources.map(s => (
                  <div key={s.name} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-[#0d121b] dark:text-white truncate max-w-[65%]">{s.name}</span>
                    <span className="text-xs font-bold text-[#2b6cee] bg-[#2b6cee]/10 px-2 py-0.5 rounded-full">
                      {s.clicks.toLocaleString()}
                    </span>
                  </div>
                ))}
                {breakdowns!.utm.sources.length === 0 && (
                  <p className="text-xs text-[#4c669a]">No data</p>
                )}
              </div>
            </div>
            {/* Mediums */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-[#4c669a] dark:text-gray-400 mb-3">Mediums</h4>
              <div className="space-y-2">
                {breakdowns!.utm.mediums.map(m => (
                  <div key={m.name} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-[#0d121b] dark:text-white truncate max-w-[65%]">{m.name}</span>
                    <span className="text-xs font-bold text-[#10b981] bg-[#10b981]/10 px-2 py-0.5 rounded-full">
                      {m.clicks.toLocaleString()}
                    </span>
                  </div>
                ))}
                {breakdowns!.utm.mediums.length === 0 && (
                  <p className="text-xs text-[#4c669a]">No data</p>
                )}
              </div>
            </div>
            {/* Campaigns */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-[#4c669a] dark:text-gray-400 mb-3">Campaigns</h4>
              <div className="space-y-2">
                {breakdowns!.utm.campaigns.map(c => (
                  <div key={c.name} className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-[#0d121b] dark:text-white truncate max-w-[65%]">{c.name}</span>
                    <span className="text-xs font-bold text-[#8b5cf6] bg-[#8b5cf6]/10 px-2 py-0.5 rounded-full">
                      {c.clicks.toLocaleString()}
                    </span>
                  </div>
                ))}
                {breakdowns!.utm.campaigns.length === 0 && (
                  <p className="text-xs text-[#4c669a]">No data</p>
                )}
              </div>
            </div>
          </div>
        </SectionCard>
      )}

      {/* ── Top Links + Recent Activity ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Links - enhanced */}
        <SectionCard title="Top Performing Links" icon="leaderboard">
          {topLinksError ? (
            <p className="text-red-500 text-sm">Failed to load top links</p>
          ) : topLinks && topLinks.length > 0 ? (
            <div className="space-y-1">
              {topLinks.map((link, i) => (
                <div
                  key={link.shortcode}
                  className="flex items-center gap-3 py-3 border-b border-gray-50 dark:border-white/5 last:border-0 group"
                >
                  <span className="w-6 h-6 rounded-full bg-[#2b6cee]/10 text-[#2b6cee] text-xs font-bold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#2b6cee] truncate">{link.shortcode}</p>
                    <p className="text-xs text-[#4c669a] dark:text-gray-500 truncate">{link.original_url}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-[#0d121b] dark:text-white">{link.clicks.toLocaleString()}</p>
                    <p className="text-[10px] text-[#4c669a] dark:text-gray-500">clicks</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#4c669a] dark:text-gray-500">No link data available</p>
          )}
        </SectionCard>

        {/* Recent Activity Feed */}
        <SectionCard title="Recent Activity" icon="history">
          {recentClicks && recentClicks.length > 0 ? (
            <div className="space-y-1 max-h-[360px] overflow-y-auto">
              {recentClicks.map((click, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 py-2.5 border-b border-gray-50 dark:border-white/5 last:border-0"
                >
                  <span className="text-base w-6 text-center shrink-0">
                    {countryFlag(click.country_code) || (
                      <span className="material-symbols-outlined text-[16px] text-[#4c669a]">language</span>
                    )}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {click.shortcode && (
                        <span className="text-xs font-medium text-[#2b6cee]">/{click.shortcode}</span>
                      )}
                      {click.city && click.country && (
                        <span className="text-xs text-[#4c669a]">{click.city}, {click.country}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      {click.device_type && (
                        <span className="text-[10px] text-[#4c669a]/70 capitalize">{click.device_type}</span>
                      )}
                      {click.browser && (
                        <span className="text-[10px] text-[#4c669a]/70">{click.browser}</span>
                      )}
                      {click.os && (
                        <span className="text-[10px] text-[#4c669a]/70">{click.os}</span>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] text-[#4c669a] dark:text-gray-500 shrink-0 whitespace-nowrap">
                    {timeAgo(click.clicked_at)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#4c669a] dark:text-gray-500">No recent activity yet</p>
          )}
        </SectionCard>
      </div>
    </div>
  )
}
